import {
  Box,
  Button,
  CloseButton,
  Drawer,
  IconButton,
  Menu,
  Portal,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toasterApi } from '@ui';
import React from 'react';
import { LuEye, LuFileText, LuPrinter, LuQrCode, LuTag } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import { PermissionCheck } from '@/components';
import { TrackingApi } from '@/state/api';
import { useAppSelector } from '@/state';
import { TrackingType } from '@/types';
import { useLabelComposer } from './composer/LabelComposerProvider';
import { ItemReportView } from './report/ItemReportView';
import { openPdfBlob, printPdfBlob } from './utils';

type ActionSource = TrackingType.ComposerSource & { label: string };

interface TrackingActionsProps {
  source: ActionSource;
  context: {
    page: string;
    project_id?: number | null;
    release_id?: number | null;
  };
  size?: 'xs' | 'sm';
  /** Render as a compact icon menu for dense table rows. */
  compact?: boolean;
  /** Unsaved records cannot receive permanent labels. */
  disabledReason?: string;
}

const lookupParams = (source: ActionSource) =>
  source.source_type === 'frame'
    ? { frame_id: source.source_id, limit: 100 }
    : {
        source_type: source.source_type,
        source_id: source.source_id,
        limit: 25,
      };

const TrackingPanel: React.FC<{ code: string; onClose: () => void }> = ({
  code,
  onClose,
}) => {
  const navigate = useNavigate();
  const { data, isFetching, refetch } = TrackingApi.Api.useGetReportQuery(
    { code },
    { refetchOnMountOrArgChange: true },
  );
  return (
    <Drawer.Root
      open
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      size={{ base: 'full', md: 'lg' }}
      placement="end"
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header borderBottomWidth="1px">
              <Drawer.Title>Tracking</Drawer.Title>
              <Drawer.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body p={2}>
              {!data ? (
                <Spinner />
              ) : (
                <ItemReportView
                  report={data}
                  actions={
                    <Stack direction="row" gap={2} flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => refetch()}
                        loading={isFetching}
                      >
                        Refresh
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => navigate(`/t/${code}`)}
                      >
                        Open full report
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => navigate(`/tracking/items/${code}`)}
                      >
                        Tracking details
                      </Button>
                    </Stack>
                  }
                />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export const TrackingActions: React.FC<TrackingActionsProps> = ({
  source,
  context,
  size = 'xs',
  compact,
  disabledReason,
}) => {
  const composer = useLabelComposer();
  const navigate = useNavigate();
  const permissions = useAppSelector(
    (s) => s.auth.current.user?.permissions ?? [],
  ) as string[];
  const canPrint = permissions.includes('print_trackeditem');
  const [lookup, { isFetching }] = TrackingApi.Api.useLazyGetItemsQuery();
  const [getPdf] = TrackingApi.Api.useGetReportPdfMutation();
  const [panelCode, setPanelCode] = React.useState<string | null>(null);
  const [mobilePdf, setMobilePdf] = React.useState<Blob | null>(null);

  const findIdentity = async () => {
    const res = await lookup(lookupParams(source))
      .unwrap()
      .catch(() => null);
    return res?.items ?? [];
  };

  const viewTracking = async () => {
    const items = await findIdentity();
    if (!items.length) {
      toasterApi({
        type: 'info',
        status: 200,
        code: 'Info',
        message:
          'This record has no tracking identity yet. Print a label to register it',
      });
      return;
    }
    if (items.length === 1) setPanelCode(items[0].code);
    else
      navigate(
        source.source_type === 'frame'
          ? `/tracking?tab=items&frame_id=${source.source_id}`
          : `/tracking?tab=items&source_type=${source.source_type}&source_id=${source.source_id}`,
      );
  };

  const printReport = async () => {
    const items = await findIdentity();
    if (!items.length) {
      toasterApi({
        type: 'info',
        status: 200,
        code: 'Info',
        message: 'This record has no tracking identity yet',
      });
      return;
    }
    if (items.length > 1) {
      composer.open({
        codes: items.map((i) => i.code),
        context,
        mode: 'reports',
        title: source.label,
      });
      return;
    }
    try {
      const pdf = await getPdf({
        code: items[0].code,
        paper: 'letter',
      }).unwrap();
      if (!printPdfBlob(pdf)) setMobilePdf(pdf);
    } catch {
      /* reported globally */
    }
  };

  const printLabel = () =>
    composer.open({ sources: [source], context, title: source.label });

  return (
    <PermissionCheck permissions={['view_trackeditem']}>
      <Menu.Root positioning={{ placement: 'bottom-end' }}>
        <Menu.Trigger asChild>
          {compact ? (
            <IconButton
              size={size}
              variant="ghost"
              aria-label={`Tracking for ${source.label}`}
              loading={isFetching}
            >
              <LuQrCode />
            </IconButton>
          ) : (
            <Button size={size} variant="outline" loading={isFetching}>
              <LuQrCode /> Tracking
            </Button>
          )}
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content minW="220px">
              {canPrint && (
                <Menu.Item
                  value="print"
                  onClick={printLabel}
                  disabled={Boolean(disabledReason)}
                >
                  <LuTag />{' '}
                  {source.source_type === 'frame'
                    ? 'Print unit labels'
                    : 'Print label'}
                </Menu.Item>
              )}
              <Menu.Item value="view" onClick={viewTracking}>
                <LuEye /> View tracking
              </Menu.Item>
              {canPrint && (
                <Menu.Item value="report" onClick={printReport}>
                  <LuFileText /> Print latest report
                </Menu.Item>
              )}
              {disabledReason && (
                <Box px={3} py={1.5}>
                  <Text fontSize="xs" color="fg.muted">
                    {disabledReason}
                  </Text>
                </Box>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      {panelCode && (
        <TrackingPanel code={panelCode} onClose={() => setPanelCode(null)} />
      )}
      {mobilePdf && (
        <Button
          size={size}
          colorPalette="blue"
          onClick={() => {
            openPdfBlob(mobilePdf);
            setMobilePdf(null);
          }}
        >
          <LuPrinter /> Open report PDF
        </Button>
      )}
    </PermissionCheck>
  );
};

/** Toolbar action for multiple selected source rows. */
export const PrintSelectedLabelsButton: React.FC<{
  sources: ActionSource[];
  context: TrackingActionsProps['context'];
  size?: 'xs' | 'sm';
}> = ({ sources, context, size = 'sm' }) => {
  const composer = useLabelComposer();
  return (
    <PermissionCheck permissions={['print_trackeditem']}>
      <Button
        size={size}
        variant="outline"
        disabled={!sources.length}
        onClick={() =>
          composer.open({
            sources,
            context,
            title: `${sources.length} selected record${sources.length === 1 ? '' : 's'}`,
          })
        }
      >
        <LuTag /> Print selected labels
        {sources.length ? ` (${sources.length})` : ''}
      </Button>
    </PermissionCheck>
  );
};
